import { useState, useRef, useCallback, useEffect } from 'react';
import { fetchWidgetData } from '../../../../services/widgetDataService';
import { WIDGET_LAZY_LOAD_ROOT_MARGIN } from '../../constants';

const MAX_RETRIES = 3;
const RETRY_THROTTLE_MS = 2000;

export const useWidgetData = ({
  instanceUuid,
  scopeContext,
  dashboardUuid,
  activeFilters,
  acceptsGlobalFilters,
}) => {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const containerRef = useRef(null);
  const fetchIdRef = useRef(0);
  const refetchControllerRef = useRef(null);
  const lastRetryAtRef = useRef(0);

  const performFetch = useCallback(
    async (signal, { isRetry = false, widgetContext = null } = {}) => {
      const requestId = ++fetchIdRef.current;
      setLoading(true);
      setError(null);

      try {
        const dashboardContext = {
          dashboard_uuid: dashboardUuid,
          active_filters: acceptsGlobalFilters ? (activeFilters || null) : null,
        };

        const result = await fetchWidgetData(
          instanceUuid,
          scopeContext,
          dashboardContext,
          widgetContext,
          { signal },
        );

        if (requestId !== fetchIdRef.current || signal?.aborted) return;

        // The contract always returns 200. Datasource errors come in meta.error.
        if (result.meta?.error) {
          setError({ message: result.meta.error, isDatasourceError: true });
          setData(null);
          setMeta(result.meta);
        } else {
          setData(result.data);
          setMeta(result.meta);
          setRetryCount(0);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (requestId === fetchIdRef.current) {
          setError(err);
          setData(null);
          setMeta(null);
          if (isRetry) {
            setRetryCount((prev) => prev + 1);
          }
        }
      } finally {
        if (requestId === fetchIdRef.current && !signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [instanceUuid, scopeContext, dashboardUuid, activeFilters, acceptsGlobalFilters],
  );

  useEffect(() => {
    setRetryCount(0);
  }, [scopeContext]);

  useEffect(() => {
    if (!instanceUuid || !scopeContext) return;

    const element = containerRef.current;
    if (!element) return;

    const controller = new AbortController();
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;

        observer.disconnect();
        performFetch(controller.signal, { isRetry: false });
      },
      {
        rootMargin: WIDGET_LAZY_LOAD_ROOT_MARGIN,
        threshold: 0,
      },
    );

    observer.observe(element);

    return () => {
      controller.abort();
      observer.disconnect();
    };
  }, [instanceUuid, scopeContext, performFetch]);

  const isEmpty = meta?.is_empty === true;
  const canRetry = retryCount < MAX_RETRIES && !loading;

  const refetch = useCallback(
    (widgetContext = null) => {
      if (loading) return;
      const now = Date.now();
      if (now - lastRetryAtRef.current < RETRY_THROTTLE_MS) return;
      if (error && retryCount >= MAX_RETRIES) return;

      lastRetryAtRef.current = now;
      refetchControllerRef.current?.abort();
      const controller = new AbortController();
      refetchControllerRef.current = controller;
      performFetch(controller.signal, { isRetry: !!error, widgetContext });
    },
    [performFetch, loading, error, retryCount],
  );

  useEffect(() => {
    return () => {
      refetchControllerRef.current?.abort();
    };
  }, []);

  return {
    data,
    meta,
    loading,
    error,
    isEmpty,
    refetch,
    canRetry,
    containerRef,
  };
};
